/**
 * Motor de avisos.
 *
 * ── LAS REGLAS DE HIGIENE ───────────────────────────────────────────────────
 *
 * Son más importantes que las reglas de contenido. Una app que avisa de todo
 * se vuelve ruido, y el ruido se ignora — incluso el aviso que sí importaba.
 *
 *   1. Máximo TRES avisos a la vez. Siempre los más graves.
 *   2. Todo aviso negativo trae una acción concreta. Decir "gastaste mucho"
 *      sin decir qué hacer solo produce culpa.
 *   3. Un aviso descartado no vuelve en varios días.
 *   4. Al menos una cosa buena cuando la haya. Si solo se regaña, se cierra.
 *   5. Nada de porcentajes sin pesos al lado: "vas en el 78%" no dice nada,
 *      "te quedan $60.000" sí.
 *
 * Los cálculos son locales. No se manda nada a ningún servidor para esto:
 * son sumas y restas, no hace falta IA, y así funciona sin internet.
 */

import { formatear } from './dinero.js';
import { aportesOps, fechaLimitePila } from './parametros.js';
import { planDeCuotas, paramsDe, estadoPlan, liquidarHoy, cuotasDelMes } from './cuotas.js';
import { estadoDe, fechaDePrestamo } from './deudas.js';
import { resumenFijos, porPagar, proximosCobros, costoMensual, COBRO } from './recurrentes.js';

export const GRAVEDAD = {
  CELEBRACION: 0,
  INFORMATIVO: 1,
  ATENCION: 2,
  URGENTE: 3,
};

const DIA = 86400000;

function hoyISO() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function diasDelMes(fecha = new Date()) {
  return new Date(fecha.getFullYear(), fecha.getMonth() + 1, 0).getDate();
}

/**
 * Arma la foto financiera del momento.
 * Todas las reglas leen de aquí, así nadie recalcula lo mismo dos veces.
 */
export function fotografiar({
  movimientos = [], cuentas = [], categorias = [], tarjetas = [],
  presupuestos = [], contratos = [], prestamos = [], compras = [], abonos = [],
  recurrentes = [],
  compromisosMes = 0,   // lo que falta pagar este mes (lib/libre.js)
  hoy = null,   // 'YYYY-MM-DD'. Solo lo pasan las pruebas, para fijar el día.
}) {
  const hoyTexto = hoy ?? hoyISO();
  // Todo se mide contra el MISMO día: el de hoy, o el que fije una prueba.
  const ahora = new Date(hoyTexto + 'T12:00:00');
  const mes = hoyTexto.slice(0, 7);
  const diaDeHoy = ahora.getDate();
  const total = diasDelMes(ahora);
  const quedan = total - diaDeHoy;

  const vivos = movimientos.filter((m) => !m.borrado_en);
  const delMes = vivos.filter((m) => m.fecha?.startsWith(mes));

  const ingresos = delMes.filter((m) => m.tipo === 'ingreso').reduce((t, m) => t + m.monto, 0);
  const egresos = delMes.filter((m) => m.tipo === 'egreso').reduce((t, m) => t + m.monto, 0);

  const catPorId = Object.fromEntries(categorias.map((c) => [c.id, c]));

  // Cuánta plata hay de verdad disponible: efectivo, bancos y billeteras.
  // Las tarjetas no cuentan: el cupo no es plata tuya, es plata prestada.
  const LIQUIDAS = ['efectivo', 'ahorros', 'corriente', 'billetera'];
  const saldos = {};
  for (const c of cuentas) saldos[c.id] = c.saldo_inicial || 0;
  for (const m of vivos) {
    if (m.cuenta_origen && saldos[m.cuenta_origen] !== undefined) saldos[m.cuenta_origen] -= m.monto;
    if (m.cuenta_destino && saldos[m.cuenta_destino] !== undefined) saldos[m.cuenta_destino] += m.monto;
  }
  const disponible = cuentas
    .filter((c) => LIQUIDAS.includes(c.tipo) && !c.archivada)
    .reduce((t, c) => t + (saldos[c.id] ?? 0), 0);

  // Lo que queda después de lo que falta pagar este mes. Con esto se decide
  // si alcanza: la plata de la cuota de Addi ya tiene dueño.
  const libre = disponible - compromisosMes;

  // Gasto diario promedio de lo que va del mes.
  const gastoDiario = diaDeHoy > 0 ? egresos / diaDeHoy : 0;

  // Gastos por categoría de este mes y del anterior, para comparar.
  const porCategoria = {};
  for (const m of delMes) {
    if (m.tipo !== 'egreso') continue;
    const nombre = catPorId[m.categoria_id]?.nombre ?? 'Sin categoría';
    porCategoria[nombre] = (porCategoria[nombre] ?? 0) + m.monto;
  }

  const mesPasado = new Date(ahora.getFullYear(), ahora.getMonth() - 1, 1)
    .toISOString().slice(0, 7);
  const delMesPasado = vivos.filter((m) => m.fecha?.startsWith(mesPasado));
  const egresosMesPasado = delMesPasado.filter((m) => m.tipo === 'egreso')
    .reduce((t, m) => t + m.monto, 0);

  const gmfDelMes = delMes
    .filter((m) => m.concepto === '4x1000')
    .reduce((t, m) => t + m.monto, 0);

  const gmfDelAnio = vivos
    .filter((m) => m.fecha?.startsWith(mes.slice(0, 4)) && m.concepto === '4x1000')
    .reduce((t, m) => t + m.monto, 0);

  /**
   * Las compras a cuotas, ya con su calendario resuelto.
   *
   * Se calcula una sola vez aqui y no dentro de cada regla: son tres reglas
   * las que lo necesitan, y armar el plan de cuotas de cada compra tres veces
   * seria trabajo repetido en cada repintado de la pantalla.
   */
  const comprasVivas = compras
    .filter((c) => !c.borrado_en && !c.pagada)
    .map((c) => {
      const plan = c.plan?.length
        ? c.plan
        : planDeCuotas(paramsDe(c, tarjetas.find((t) => t.cuenta_id === c.cuenta_id) ?? null));
      const propios = abonos.filter((a) => a.compra_id === c.id && !a.borrado_en);
      return {
        ...c, plan, abonos: propios,
        estado: estadoPlan(plan, propios, hoyTexto),
        liquidacion: liquidarHoy(plan, propios, hoyTexto),
      };
    });

  const cuotasMes = cuotasDelMes(comprasVivas, hoyTexto.slice(0, 7));

  // Suscripciones y pagos fijos. Lo que ya se pagó a mano se reconoce por el
  // id fijo de su cobro, que está entre los movimientos cargados.
  const vivasRec = recurrentes.filter((r) => !r.borrado_en);
  const fijos = resumenFijos(vivasRec, hoyTexto);
  const idsMovimientos = new Set(movimientos.map((m) => m.id));
  const fijosPorPagar = porPagar(vivasRec, idsMovimientos, hoyTexto);
  const debitosCercanos = proximosCobros(
    vivasRec.filter((r) => r.cobro === COBRO.DEBITO), hoyTexto, 2);

  return {
    mes, diaDeHoy, diasEnElMes: total, diasQueQuedan: quedan,
    compras: comprasVivas, cuotasMes, hoy: hoyTexto,
    fijos, fijosPorPagar, debitosCercanos,
    ingresos, egresos, balance: ingresos - egresos,
    disponible, libre, compromisosMes, gastoDiario, porCategoria,
    egresosMesPasado, gmfDelMes, gmfDelAnio,
    saldos, cuentas, tarjetas, presupuestos, categorias, contratos, prestamos,
    movimientosDelMes: delMes,
    totalMovimientos: vivos.length,
  };
}

/**
 * El catálogo de reglas.
 *
 * Cada una decide si aplica y, si aplica, devuelve el mensaje. El tono es
 * de alguien que te conoce: directo, sin regaño y sin jerga financiera.
 */
const REGLAS = [
  // ── Lo urgente: no va a alcanzar ──────────────────────────────────────────
  {
    id: 'no_alcanza',
    evaluar(f) {
      if (f.diaDeHoy < 5 || !f.gastoDiario || f.disponible <= 0) return null;
      const libre = f.libre ?? f.disponible;
      const proyectado = f.gastoDiario * f.diasQueQuedan;
      if (proyectado <= libre) return null;
      const falta = Math.round(proyectado - libre);
      return {
        gravedad: GRAVEDAD.URGENTE,
        titulo: 'Mira ve, no te va a alcanzar',
        texto: `Al ritmo que vas gastando (${formatear(Math.round(f.gastoDiario))} al día), ` +
               `te faltarían ${formatear(falta)} para llegar a fin de mes.`,
        accion: libre > 0
          ? `Bajando a ${formatear(Math.round(libre / Math.max(1, f.diasQueQuedan)))} al día, la sacas.`
          : 'Lo que tienes ya está comprometido en pagos de este mes: mira el desglose de "Libre este mes".',
      };
    },
  },
  {
    id: 'saldo_negativo',
    evaluar(f) {
      if (f.disponible >= 0) return null;
      return {
        gravedad: GRAVEDAD.URGENTE,
        titulo: 'Estás en rojo',
        texto: `Entre tus cuentas hay ${formatear(f.disponible)}.`,
        accion: 'Revisa si alguna cuenta quedó mal registrada, o si de verdad hay un sobregiro.',
      };
    },
  },

  // ── Tarjetas de crédito ───────────────────────────────────────────────────
  {
    id: 'vence_tarjeta',
    evaluar(f) {
      const hoy = new Date().getDate();
      for (const t of f.tarjetas ?? []) {
        if (!t.dia_limite_pago || t.borrado_en) continue;
        let faltan = t.dia_limite_pago - hoy;
        if (faltan < 0) faltan += f.diasEnElMes;
        if (faltan > 5) continue;

        const cuenta = f.cuentas.find((c) => c.id === t.cuenta_id);
        const deuda = Math.abs(f.saldos[t.cuenta_id] ?? 0);
        if (deuda <= 0) continue;

        const alcanza = f.disponible >= deuda;
        return {
          gravedad: faltan <= 2 ? GRAVEDAD.URGENTE : GRAVEDAD.ATENCION,
          titulo: faltan === 0
            ? `Hoy vence ${cuenta?.nombre ?? 'tu tarjeta'}`
            : `En ${faltan} día${faltan === 1 ? '' : 's'} vence ${cuenta?.nombre ?? 'tu tarjeta'}`,
          texto: `Debes ${formatear(deuda)}. Tienes ${formatear(f.disponible)} disponible.`,
          accion: alcanza
            ? 'Te alcanza. Págala completa y no generas intereses.'
            : `Te faltan ${formatear(deuda - f.disponible)}. Paga lo que más puedas: los intereses corren sobre el resto.`,
        };
      }
      return null;
    },
  },
  {
    id: 'cupo_alto',
    evaluar(f) {
      for (const c of f.cuentas) {
        if (!c.cupo_total || c.archivada) continue;
        const usado = Math.abs(f.saldos[c.id] ?? 0);
        const pct = (usado / c.cupo_total) * 100;
        const tope = c.alerta_cupo_pct ?? 70;
        if (pct < tope) continue;
        return {
          gravedad: pct >= 90 ? GRAVEDAD.ATENCION : GRAVEDAD.INFORMATIVO,
          titulo: `${c.nombre} va en el ${pct.toFixed(0)}% del cupo`,
          texto: `Usaste ${formatear(usado)} de ${formatear(c.cupo_total)}. ` +
                 `Te quedan ${formatear(c.cupo_total - usado)}.`,
          accion: 'Usar más del 70% del cupo baja tu puntaje en las centrales de riesgo.',
        };
      }
      return null;
    },
  },

  // ── El 4x1000 ─────────────────────────────────────────────────────────────
  {
    id: 'gmf_acumulado',
    evaluar(f) {
      if (f.gmfDelAnio < 20000) return null;
      return {
        gravedad: GRAVEDAD.INFORMATIVO,
        titulo: `Llevas ${formatear(f.gmfDelAnio)} en 4x1000 este año`,
        texto: 'Es plata que no compró nada. Cada traslado entre cuentas te cuesta 0,4%.',
        accion: 'Haz menos traslados y más grandes. Y revisa que tengas marcada tu cuenta exenta.',
      };
    },
  },
  {
    id: 'sin_cuenta_exenta',
    evaluar(f) {
      if (f.gmfDelMes < 3000) return null;
      const tieneExenta = f.cuentas.some((c) => c.exenta_gmf && !c.archivada);
      if (tieneExenta) return null;
      return {
        gravedad: GRAVEDAD.ATENCION,
        titulo: 'No tienes marcada tu cuenta exenta',
        texto: `Este mes llevas ${formatear(f.gmfDelMes)} en 4x1000. ` +
               'La ley te deja marcar una cuenta y mover hasta $18.330.900 al mes sin pagarlo.',
        accion: 'Pídele a tu banco que marque la cuenta, y actívala aquí en Cuentas.',
      };
    },
  },

  // ── Gastos que se dispararon ──────────────────────────────────────────────
  {
    id: 'comida_fuera',
    evaluar(f) {
      const comida = (f.porCategoria['Comida fuera'] ?? 0) + (f.porCategoria['Domicilios'] ?? 0);
      if (comida < 80000 || f.diaDeHoy < 5) return null;
      const alDia = comida / f.diaDeHoy;
      const proyectado = Math.round(alDia * f.diasEnElMes);
      if (proyectado < 200000) return null;
      return {
        gravedad: GRAVEDAD.ATENCION,
        titulo: `Llevas ${formatear(comida)} en comida fuera`,
        texto: `Y van solo ${f.diaDeHoy} días. A este ritmo el mes cierra en ${formatear(proyectado)}.`,
        accion: `Cocinando la mitad de esas veces te ahorras como ${formatear(Math.round((proyectado - comida) / 2))}.`,
      };
    },
  },
  {
    id: 'mes_mas_caro',
    evaluar(f) {
      if (!f.egresosMesPasado || f.diaDeHoy < 15) return null;
      const proyectado = (f.egresos / f.diaDeHoy) * f.diasEnElMes;
      const subida = ((proyectado - f.egresosMesPasado) / f.egresosMesPasado) * 100;
      if (subida < 25) return null;
      return {
        gravedad: GRAVEDAD.INFORMATIVO,
        titulo: `Este mes va ${subida.toFixed(0)}% más caro`,
        texto: `El mes pasado gastaste ${formatear(f.egresosMesPasado)}. ` +
               `Este va camino a ${formatear(Math.round(proyectado))}.`,
        accion: 'Mira en Informes qué categoría creció; casi siempre es una sola.',
      };
    },
  },
  {
    id: 'sin_categorizar',
    evaluar(f) {
      const sin = f.porCategoria['Sin categoría'] ?? 0;
      if (!f.egresos || sin / f.egresos < 0.3 || sin < 50000) return null;
      return {
        gravedad: GRAVEDAD.INFORMATIVO,
        titulo: 'Tienes gastos sin clasificar',
        texto: `${formatear(sin)} este mes no tienen categoría, ` +
               `el ${((sin / f.egresos) * 100).toFixed(0)}% de lo que gastaste.`,
        accion: 'Clasifícalos y las gráficas empiezan a decirte algo de verdad.',
      };
    },
  },

  // ── Contratista: lo que hay que apartar ───────────────────────────────────
  {
    id: 'aportes_ops',
    evaluar(f) {
      // Se dispara cuando entra un pago grande que parece el del contrato.
      const grandes = f.movimientosDelMes.filter(
        (m) => m.tipo === 'ingreso' && m.monto >= 1500000
      );
      if (!grandes.length) return null;
      const ultimo = grandes[grandes.length - 1];

      // Se usa la función de parametros.js, no una copia de la fórmula.
      // La primera versión de esta regla repetía el cálculo a mano y se le
      // olvidó el PISO: cuando el 40% del contrato queda por debajo del
      // salario mínimo, se cotiza sobre el mínimo, no sobre el 40%. Eso
      // subestimaba el aporte en $138.000 al mes. Lo detectó Esteban
      // comparando con lo que paga de verdad.
      const a = aportesOps(ultimo.monto, ultimo.fecha);

      return {
        gravedad: GRAVEDAD.ATENCION,
        titulo: 'Aparta la seguridad social',
        texto: `De los ${formatear(ultimo.monto)} que entraron, como contratista te ` +
               `toca aportar ${formatear(a.total)} entre salud, pensión y ARL.` +
               (a.ajustadoAlPiso
                 ? ' El 40% de tu contrato queda por debajo del salario mínimo, ' +
                   'así que cotizas sobre el mínimo.'
                 : ''),
        accion: `Guárdalos ya. Lo que te queda libre de verdad son ${formatear(a.neto)}.`,
      };
    },
  },

  /**
   * La fecha límite para pagar la seguridad social.
   *
   * Es el aviso que más falta le hace a un contratista. Se paga MES VENCIDO
   * y el plazo depende de los dos últimos dígitos de la cédula, contado en
   * días hábiles. Nadie se acuerda de eso, y la multa va del 10% al 60%.
   */
  {
    id: 'vence_pila',
    evaluar(f) {
      for (const c of f.contratos ?? []) {
        if (!c.activo || c.borrado_en) continue;
        if (!c.cedula_ultimos_2 || !c.valor_mensual) continue;

        const ahora = new Date();
        const anterior = new Date(ahora.getFullYear(), ahora.getMonth() - 1, 1);
        const periodo = `${anterior.getFullYear()}-${String(anterior.getMonth() + 1).padStart(2, '0')}`;
        const limite = fechaLimitePila(c.cedula_ultimos_2, periodo);
        if (!limite?.fecha) continue;

        const dias = Math.ceil(
          (new Date(limite.fecha + 'T23:59:59') - ahora) / DIA
        );
        // Solo interesa en la ventana útil: ni antes de tiempo, ni cuando ya
        // pasó tanto que el aviso no sirve para nada.
        if (dias < -3 || dias > 6) continue;

        const a = aportesOps(c.valor_mensual);
        const vencido = dias < 0;

        return {
          gravedad: vencido || dias <= 1 ? GRAVEDAD.URGENTE : GRAVEDAD.ATENCION,
          titulo: vencido
            ? `Se venció la seguridad social de ${periodo}`
            : dias === 0
              ? 'Hoy vence tu seguridad social'
              : `En ${dias} día${dias === 1 ? '' : 's'} vence tu seguridad social`,
          texto: `Son ${formatear(a.total)} por el mes de ${periodo}. ` +
                 `Tu plazo es el día hábil ${limite.diaHabil}, por los dígitos ` +
                 `${c.cedula_ultimos_2} de tu cédula.`,
          accion: vencido
            ? 'Págala cuanto antes: la sanción va del 10% al 60% de lo no pagado.'
            : `Tienes ${formatear(f.disponible)} disponible. Paga por PILA antes de esa fecha.`,
        };
      }
      return null;
    },
  },

  /**
   * Plata prestada que lleva mucho tiempo afuera.
   *
   * No es un regaño: es que uno presta y se le olvida. Ver la cifra y los
   * dias juntos es lo que hace acordarse de cobrar.
   */
  {
    id: 'prestamo_viejo',
    evaluar(f) {
      const candidatos = (f.prestamos ?? [])
        .filter((p) => p.me_deben && estadoDe(p) === 'activa' && !p.borrado_en && p.saldo_pendiente > 0)
        .map((p) => {
          // `fecha_prestamo` es el dia en que salio la plata. Antes esto leia
          // `creado_en`, que NO es un campo de la tabla: siempre daba undefined
          // y los prestamos sin fecha acordada no avisaban nunca.
          const ref = p.proxima_fecha_pago || fechaDePrestamo(p);
          const dias = ref
            ? Math.floor((Date.now() - new Date(String(ref).slice(0, 10) + 'T12:00:00')) / DIA)
            : 0;
          return { ...p, dias, conFecha: Boolean(p.proxima_fecha_pago) };
        })
        // Con fecha acordada: cuando ya se paso. Sin fecha: a los 30 dias.
        .filter((p) => (p.conFecha ? p.dias > 0 : p.dias >= 30))
        .sort((a, b) => b.saldo_pendiente - a.saldo_pendiente);

      if (!candidatos.length) return null;
      const p = candidatos[0];
      const total = candidatos.reduce((t, x) => t + x.saldo_pendiente, 0);

      return {
        gravedad: GRAVEDAD.INFORMATIVO,
        titulo: p.conFecha
          ? `${p.acreedor} se pasó de la fecha`
          : `${p.acreedor} te debe hace ${p.dias} días`,
        texto: `Son ${formatear(p.saldo_pendiente)}.` +
               (candidatos.length > 1
                 ? ` Entre ${candidatos.length} personas te deben ${formatear(total)}.`
                 : ''),
        accion: 'Es plata tuya que está por fuera. Un recordatorio no le cae mal a nadie.',
      };
    },
  },


  // ── Compras a cuotas ──────────────────────────────────────────────────────
  {
    id: 'cuota_vencida',
    evaluar(f) {
      const atrasadas = (f.compras ?? [])
        .filter((c) => c.estado.enMora)
        .sort((a, b) => b.estado.diasMora - a.estado.diasMora);
      if (!atrasadas.length) return null;

      const total = atrasadas.reduce(
        (t, c) => t + c.estado.vencidas.reduce((s, v) => s + v.pendiente, 0), 0);
      const peor = atrasadas[0];

      return {
        gravedad: GRAVEDAD.URGENTE,
        titulo: atrasadas.length === 1
          ? `Se te pasó la cuota de ${peor.descripcion}`
          : `Tienes ${atrasadas.length} compras atrasadas`,
        texto: `Son ${formatear(total)}, y la más vieja lleva ${peor.estado.diasMora} `
             + `día${peor.estado.diasMora === 1 ? '' : 's'}.`,
        accion: f.disponible >= total
          ? 'Te alcanza. Págala hoy: los días de mora se reportan a las centrales de riesgo.'
          : `Tienes ${formatear(f.disponible)}. Abona lo que puedas; lo que cuenta es dejar de sumar días.`,
      };
    },
  },
  {
    id: 'cuota_proxima',
    evaluar(f) {
      // Se mira la PRÓXIMA cuota de cada compra, no las cuotas "del mes".
      // La primera versión leía cuotasMes y fallaba justo a fin de mes: una
      // cuota que vence el 2 del mes siguiente no es "de este mes", así que
      // el día 30 no avisaba de algo que vencía en tres días.
      const hoy = f.hoy ?? hoyISO();
      const proximas = (f.compras ?? [])
        .filter((c) => c.estado.proxima && !c.estado.enMora)
        .map((c) => ({ c, cuota: c.estado.proxima }))
        .filter((x) => x.cuota.fecha >= hoy)
        .sort((a, b) => a.cuota.fecha.localeCompare(b.cuota.fecha));
      if (!proximas.length) return null;

      const fecha = proximas[0].cuota.fecha;
      const dias = Math.round(
        (new Date(fecha + 'T12:00:00') - new Date(hoy + 'T12:00:00')) / DIA);
      if (dias > 4) return null;

      // Todo lo que cae el mismo día va junto: en la cabeza es un solo pago.
      const delDia = proximas.filter((x) => x.cuota.fecha === fecha);
      const monto = delDia.reduce((t, x) => t + x.cuota.pendiente, 0);
      const una = delDia[0];

      return {
        gravedad: dias <= 1 ? GRAVEDAD.ATENCION : GRAVEDAD.INFORMATIVO,
        titulo: dias === 0
          ? 'Hoy vence una cuota'
          : dias === 1
            ? 'Mañana vence una cuota'
            : `En ${dias} días vence una cuota`,
        texto: delDia.length === 1
          ? `${formatear(monto)} de ${una.c.descripcion}, cuota ${una.cuota.numero} de ${una.c.estado.cuotasTotales}.`
          : `${formatear(monto)} entre ${delDia.length} cuotas del mismo día.`,
        accion: f.disponible >= monto
          ? `Tienes ${formatear(f.disponible)}. Sepáralos y no lo pienses más.`
          : `Solo tienes ${formatear(f.disponible)}. Te faltan ${formatear(monto - f.disponible)}.`,
      };
    },
  },
  {
    id: 'cuotas_pesadas',
    evaluar(f) {
      if (!f.ingresos || !f.cuotasMes?.total) return null;
      const pct = (f.cuotasMes.total / f.ingresos) * 100;
      if (pct < 30) return null;
      return {
        gravedad: GRAVEDAD.ATENCION,
        titulo: `Las cuotas se llevan el ${pct.toFixed(0)}% de lo que entra`,
        texto: `Este mes van ${formatear(f.cuotasMes.total)} en cuotas, de `
             + `${formatear(f.ingresos)} que entraron.`,
        accion: 'Por encima del 30% ya aprieta. No difieras nada más hasta que bajen.',
      };
    },
  },
  {
    id: 'puedes_liquidar',
    evaluar(f) {
      // La guarda del 50% es deliberada: decirle a alguien que se gaste toda
      // la plata que tiene para ahorrarse intereses es un mal consejo, por
      // buena que sea la aritmética. Solo se sugiere si de verdad le sobra.
      const candidatas = (f.compras ?? [])
        .filter((c) => c.liquidacion.ahorro >= 5000
                    && c.liquidacion.capital > 0
                    && c.liquidacion.capital <= f.disponible * 0.5)
        .sort((a, b) => b.liquidacion.ahorro - a.liquidacion.ahorro);
      if (!candidatas.length) return null;
      const c = candidatas[0];

      return {
        gravedad: GRAVEDAD.INFORMATIVO,
        titulo: `Puedes cerrar ${c.descripcion} y ahorrarte ${formatear(c.liquidacion.ahorro)}`,
        texto: `Pagando ${formatear(c.liquidacion.capital)} hoy queda saldada, en vez de `
             + `${formatear(c.liquidacion.siPagaCuotas)} entre las ${c.liquidacion.cuotasQueEvita} cuotas que faltan.`,
        accion: 'Prepagar es un derecho tuyo por ley y no puede tener penalidad (Ley 1555 de 2012).',
      };
    },
  },
  {
    id: 'ultima_cuota',
    evaluar(f) {
      const casi = (f.compras ?? []).filter(
        (c) => c.estado.cuotasTotales > 1
            && c.estado.cuotasPagadas === c.estado.cuotasTotales - 1
            && !c.estado.enMora);
      if (!casi.length) return null;
      const c = casi[0];
      return {
        gravedad: GRAVEDAD.CELEBRACION,
        titulo: `Última cuota de ${c.descripcion}`,
        texto: `Quedan ${formatear(c.estado.pendiente)} y queda saldada.`,
        accion: `Después de esta te liberas ${formatear(c.valor_cuota)} al mes.`,
      };
    },
  },

  // ── Suscripciones y pagos fijos ───────────────────────────────────────────
  {
    id: 'debito_sin_saldo',
    evaluar(f) {
      // Un débito automático sin saldo rebota: además del cobro, llega la
      // multa o se suspende el servicio. Es de los pocos avisos que valen
      // la pena aunque falten dos días.
      for (const c of f.debitosCercanos ?? []) {
        const saldo = f.saldos?.[c.rec.cuenta_id];
        if (saldo === undefined || saldo >= c.monto) continue;
        const cuenta = f.cuentas.find((x) => x.id === c.rec.cuenta_id);
        return {
          gravedad: GRAVEDAD.URGENTE,
          titulo: `${c.dias === 0 ? 'Hoy' : c.dias === 1 ? 'Mañana' : `En ${c.dias} días`} te debitan ${c.rec.nombre} y no hay saldo`,
          texto: `En ${cuenta?.nombre ?? 'la cuenta'} hay ${formatear(saldo)} y el cobro es de ${formatear(c.monto)}.`,
          accion: `Pásale ${formatear(c.monto - saldo)} antes, o el cobro rebota.`,
        };
      }
      return null;
    },
  },
  {
    id: 'fijo_por_pagar',
    evaluar(f) {
      const p = (f.fijosPorPagar ?? [])[0];
      if (!p) return null;
      const n = f.fijosPorPagar.length;
      const cuando = p.dias < 0
        ? `Se pasó el pago de ${p.rec.nombre}`
        : p.dias === 0 ? `Hoy toca pagar ${p.rec.nombre}`
        : p.dias === 1 ? `Mañana toca pagar ${p.rec.nombre}`
        : `En ${p.dias} días toca pagar ${p.rec.nombre}`;
      return {
        gravedad: p.dias < 0 ? GRAVEDAD.URGENTE : p.dias <= 1 ? GRAVEDAD.ATENCION : GRAVEDAD.INFORMATIVO,
        titulo: cuando,
        texto: `Son ${formatear(p.rec.monto)}.` +
               (n > 1 ? ` Y hay ${n - 1} pago${n - 1 === 1 ? '' : 's'} fijo${n - 1 === 1 ? '' : 's'} más por hacer.` : ''),
        accion: f.disponible >= p.rec.monto
          ? 'Cuando lo pagues, márcalo en Fijos con "Ya lo pagué".'
          : `Tienes ${formatear(f.disponible)} disponible: no alcanza completo.`,
      };
    },
  },
  {
    id: 'prueba_por_vencer',
    evaluar(f) {
      const p = f.fijos?.pruebasPorVencer?.[0];
      if (!p) return null;
      return {
        gravedad: GRAVEDAD.ATENCION,
        titulo: p.dias <= 0
          ? `Hoy termina la prueba gratis de ${p.rec.nombre}`
          : `La prueba gratis de ${p.rec.nombre} termina ${p.dias === 1 ? 'mañana' : `en ${p.dias} días`}`,
        texto: `Después te cobra ${formatear(p.rec.monto)} cada vez: ` +
               `${formatear(costoMensual(p.rec) * 12)} al año.`,
        accion: 'Si no la vas a usar, cancélala antes de esa fecha. Después ya no te devuelven.',
      };
    },
  },
  {
    id: 'fijos_pesan',
    evaluar(f) {
      if (!f.ingresos || !f.fijos?.mensual || f.diaDeHoy < 5) return null;
      const pct = (f.fijos.mensual / f.ingresos) * 100;
      if (pct < 25) return null;
      return {
        gravedad: GRAVEDAD.INFORMATIVO,
        titulo: `Los pagos fijos se llevan el ${pct.toFixed(0)}% de lo que entra`,
        texto: `Son ${formatear(f.fijos.mensual)} al mes: ${formatear(f.fijos.anual)} al año.`,
        accion: 'Revisa en Fijos cuál no estás usando. Cancelar una sola ya se nota.',
      };
    },
  },

  // ── Cosas buenas ──────────────────────────────────────────────────────────
  {
    id: 'buen_ahorro',
    evaluar(f) {
      if (f.ingresos <= 0 || f.diaDeHoy < 20) return null;
      const tasa = (f.balance / f.ingresos) * 100;
      if (tasa < 20) return null;
      return {
        gravedad: GRAVEDAD.CELEBRACION,
        titulo: `Vas ahorrando el ${tasa.toFixed(0)}%`,
        texto: `De ${formatear(f.ingresos)} que entraron, llevas ${formatear(f.balance)} sin gastar.`,
        accion: 'Eso está por encima de lo que recomiendan. Bien ahí.',
      };
    },
  },
  {
    id: 'mes_mas_barato',
    evaluar(f) {
      if (!f.egresosMesPasado || f.diaDeHoy < 20) return null;
      const proyectado = (f.egresos / f.diaDeHoy) * f.diasEnElMes;
      const bajada = ((f.egresosMesPasado - proyectado) / f.egresosMesPasado) * 100;
      if (bajada < 15) return null;
      return {
        gravedad: GRAVEDAD.CELEBRACION,
        titulo: `Este mes va ${bajada.toFixed(0)}% más barato`,
        texto: `Vas camino a gastar ${formatear(Math.round(proyectado))}, ` +
               `contra ${formatear(f.egresosMesPasado)} del mes pasado.`,
        accion: `Si cierras así, te quedan ${formatear(Math.round(f.egresosMesPasado - proyectado))} de más.`,
      };
    },
  },
  {
    id: 'constancia',
    evaluar(f) {
      const dias = new Set(f.movimientosDelMes.map((m) => m.fecha)).size;
      if (dias < 7 || f.diaDeHoy < 10) return null;
      if (dias / f.diaDeHoy < 0.6) return null;
      return {
        gravedad: GRAVEDAD.CELEBRACION,
        titulo: 'Llevas el registro al día',
        texto: `Anotaste algo en ${dias} de los ${f.diaDeHoy} días del mes.`,
        accion: 'Esa constancia es la que hace que los números sirvan.',
      };
    },
  },

  // ── Para empezar ──────────────────────────────────────────────────────────
  {
    id: 'pocas_cuentas',
    evaluar(f) {
      if (f.cuentas.length > 2 || f.totalMovimientos < 5) return null;
      return {
        gravedad: GRAVEDAD.INFORMATIVO,
        titulo: 'Te faltan cuentas por agregar',
        texto: 'Solo tienes las dos que vinieron por defecto.',
        accion: 'Agrega tu Nequi, tu banco y tus tarjetas para que el patrimonio sea real.',
      };
    },
  },
];

/**
 * Evalúa todo y devuelve máximo tres avisos, los más graves primero.
 *
 * `descartados` es un mapa de id → momento en que se descartó. Un aviso
 * descartado no vuelve en tres días: repetir lo mismo que la persona ya
 * cerró es la forma más rápida de que deje de leer los avisos.
 */
export function evaluar(foto, descartados = {}) {
  const ahora = Date.now();
  const avisos = [];

  for (const regla of REGLAS) {
    const descartadoEn = descartados[regla.id];
    if (descartadoEn && ahora - descartadoEn < 3 * DIA) continue;

    let r = null;
    try {
      r = regla.evaluar(foto);
    } catch {
      // Una regla que falla no puede tumbar las demás.
      continue;
    }
    if (r) avisos.push({ id: regla.id, ...r });
  }

  avisos.sort((a, b) => b.gravedad - a.gravedad);

  // Máximo tres, y se reserva un espacio para algo bueno si lo hay: una app
  // que solo regaña se cierra y no se vuelve a abrir.
  const malos = avisos.filter((a) => a.gravedad > GRAVEDAD.CELEBRACION).slice(0, 2);
  const bueno = avisos.find((a) => a.gravedad === GRAVEDAD.CELEBRACION);

  return bueno ? [...malos, bueno] : avisos.slice(0, 3);
}
